import {IGetCheckoutShippingOptionsData} from "../../../../../typescript-library";
import {BraintreeLineItem} from "@paypal/react-paypal-js/dist/types/types/braintree/commonsTypes";
import {PayPalCheckoutCreatePaymentOptions, PayPalCheckoutUpdatePaymentOptions} from "braintree-web/paypal-checkout";
import {displayName, isoLocales} from "../../PaymentController";
import {BraintreeShippingOption} from "@paypal/react-paypal-js/dist/types/types/braintree/paypalCheckout";
import {getShopId} from "../../../getShopId";
import {convertAddress} from "./paypalAddressHelpers";
import {IPartialCartData, PaymentOptionInputParamType, PlaceType} from "./paypalTypes";
import {FUNDING_SOURCE} from "@paypal/paypal-js";
import {ICartContext} from "../../../../cart/CartContextProvider/CartContextProvider";

type LineItemKind = "debit" | "credit";

export const findShippingOption = (
  apiShippingOptions: IGetCheckoutShippingOptionsData[],
  logisticsTypeId: number | null,
): IGetCheckoutShippingOptionsData | undefined => {
  return (
    apiShippingOptions.find((shipping) => shipping.logisticsTypeId === logisticsTypeId) ||
    apiShippingOptions.find((shipping) => shipping.isBestMatch) ||
    apiShippingOptions[0]
  );
};

// region assembling paypal payment options

/**
 * Convert our shippings to braintree ones
 * @param apiShippingOptions all options as array
 * @param logisticsTypeId to set one from the array as selected
 * @param isNet to down calculate the individual shippings
 */
const gatherShippingOptions = (
  apiShippingOptions: IGetCheckoutShippingOptionsData[] | undefined,
  logisticsTypeId: number | null,
  isNet: boolean,
): BraintreeShippingOption[] | undefined => {
  let shippingOptions: BraintreeShippingOption[] | undefined;
  if (apiShippingOptions) {
    const selectedShippingOption = findShippingOption(apiShippingOptions, logisticsTypeId);

    shippingOptions = apiShippingOptions.map((shipping) => {
      let value = shipping.amount.amount;
      if (isNet) {
        value = `${Math.round(Number(shipping.amount.amount) / 0.0119) / 100}`;
      }

      return {
        id: String(shipping.logisticsTypeId),
        label: shipping.name,
        type: "SHIPPING",
        selected: shipping.logisticsTypeId === selectedShippingOption?.logisticsTypeId,
        amount: {
          value,
          currency: shipping.amount.currencyCode,
        },
      };
    });
  }

  return shippingOptions;
};

/**
 * Give amount of cart
 * @param cartData cart data containing all positions
 * @param isTotal if true: give total, if false: give total minus the shipping costs
 */
const getCartAmount = (cartData: IPartialCartData, isTotal: boolean): number => {
  let totalCartAmount = Number(cartData.value.payable.amount);
  if (!isTotal) {
    const shippingPos = cartData.positions.find((pos) => pos.type === "Shipping");
    const shippingAmount = Number(shippingPos?.amount.amount ?? "0");
    totalCartAmount = Math.round((totalCartAmount - shippingAmount) * 100) / 100;
  }

  return totalCartAmount;
};

/**
 * Build up all payment options
 * @param params `PaymentOptionInputParamType`
 */
export const getPaymentOptions = ({
  apiShippingOptions,
  cartData,
  currentLogisticsTypeId,
  deliveryAddress,
}: PaymentOptionInputParamType): PayPalCheckoutCreatePaymentOptions => {
  // gather shipping option data, if available
  const shippingOptions = gatherShippingOptions(apiShippingOptions, currentLogisticsTypeId, cartData.isNet);
  // convert address to override
  const shippingAddressOverride = convertAddress(deliveryAddress);
  // get total amount, either with or without shipping costs
  const amount = getCartAmount(cartData, !apiShippingOptions);
  // get line items, either with or without shipping costs
  const lineItems = getLineItems(cartData, !apiShippingOptions);

  /* eslint-disable @typescript-eslint/ban-ts-comment */
  return {
    // @ts-ignore
    flow: "checkout",
    amount,
    currency: cartData.currencyCode,
    displayName,
    // @ts-ignore
    intent: "authorize",
    // @ts-ignore
    shippingOptions,
    enableShippingAddress: true,
    shippingAddressEditable: !!apiShippingOptions,
    shippingAddressOverride,
    // @ts-ignore
    lineItems,
  };
  /* eslint-enable @typescript-eslint/ban-ts-comment */
};

/**
 * Builds up the line items for the order of paypal
 * @param cartData cart data containing the positions
 * @param isShippingIncluded total, including shipping, or without shipping
 */
export function getLineItems(cartData: IPartialCartData, isShippingIncluded: boolean): BraintreeLineItem[] {
  let cartDataPositions = cartData.positions.filter(({isValid}) => isValid);
  if (!isShippingIncluded) {
    cartDataPositions = cartData.positions.filter(({type, isValid}) => type !== "Shipping" && isValid);
  }
  const itemPositions = cartDataPositions.map<BraintreeLineItem>((position) => {
    const {item, price, quantity, text, type} = position;
    const numberPriceAmount = Number(price.amount);
    const tempObject = {
      kind: (quantity < 0 !== numberPriceAmount < 0 ? "credit" : "debit") as LineItemKind,
      name: text,
      quantity: Math.abs(quantity).toString(),
      unitAmount: Math.abs(numberPriceAmount).toString(),
      unitTaxAmount: undefined,
      description: item?.title,
      productCode: undefined,
      url: undefined,
    };

    if (type === "Item" && item) {
      return {...tempObject, productCode: item.itemId.toString(), url: item.url};
    }

    return tempObject;
  });

  const rebatePositions = cartData.rebates.map<BraintreeLineItem>((rebate) => {
    return {
      kind: "credit" as LineItemKind,
      name: rebate.code,
      quantity: "1",
      unitAmount: rebate.amount.amount,
      description: undefined,
      unitTaxAmount: undefined,
      productCode: undefined,
      url: undefined,
    };
  });

  return [...itemPositions, ...rebatePositions];
}

// endregion assembling paypal payment options

/**
 * Meaning: Currency is EUR/GBP/USD and country is DE/FR/US/GB
 */
export const isPayLaterAllowed = (place: PlaceType | null): boolean => {
  // Restriction via sales channel is also done by xsl
  // so there is no unnecessary blank space on the page

  return (
    place !== "pdp" &&
    ["EUR", "GBP", "USD"].includes(window.c_ActiveCurrency) &&
    ["de-DE", "fr-FR", "en-US", "en-GB"].includes(isoLocales[getShopId()].locale)
  );
};

/**
 * Locale setting is just for testing environment.
 * Should not be set in live environment
 */
export function getPayPalLocale(): string | undefined {
  let locale: string | undefined;
  if (__DEVELOPMENT__) {
    locale = isoLocales[getShopId()].locale.replace("-", "_");
  }

  return locale;
}

/**
 * Only used in the `PayPalPayLaterMessage.tsx`
 * See <https://connox.myjetbrains.com/youtrack/issue/FE-101127/PP-Pay-Later-Message-buyerCountry-wieder-einbauen>
 */
export function getBuyerCountryForMessage(): string | undefined {
  let buyerCountry: string | undefined;
  const current = isoLocales[getShopId()].locale;

  if (__DEVELOPMENT__) {
    return current.split("-")[1];
  }

  /* Price value span can be found in the live-price-volume-shipping twig */
  const useBuyerCountry = document.body.querySelector<HTMLSpanElement>("span.price-value[data-use-buyer-country]")
    ?.dataset.useBuyerCountry;
  if (useBuyerCountry?.includes(current)) {
    buyerCountry = current.split("-")[1];
  }

  return buyerCountry;
}

export function getNewPaymentOptions(
  cartData: IPartialCartData,
  logisticsTypeId: number | null,
  paymentId: string,
  apiShippingOptions?: IGetCheckoutShippingOptionsData[] | undefined,
): PayPalCheckoutUpdatePaymentOptions {
  const positionDiscount = cartData.positions.reduce<number>((acc, curr) => {
    if (["Voucher", "Discount", "CashDiscount"].includes(curr.type)) {
      acc += Math.abs(Number(curr.amount.amount));
    }

    return acc;
  }, 0.0);

  const rebateDiscount = cartData.rebates.reduce<number>(
    (acc, curr) => acc + Math.abs(Number(curr.amount.amount)),
    0.0,
  );

  const discount = String(Math.round((positionDiscount + rebateDiscount) * 100) / 100);

  return {
    paymentId,
    currency: cartData.currencyCode,
    shippingOptions: gatherShippingOptions(apiShippingOptions, logisticsTypeId, cartData.isNet),
    lineItems: getLineItems(cartData, false),
    amount: cartData.value.payable.amount,
    amountBreakdown: {
      itemTotal: String(
        cartData.positions.reduce<number>((acc, curr) => {
          if (curr.type === "Item" && !!curr.item) {
            acc += Number(curr.amount.amount);
          }

          return acc;
        }, 0.0),
      ),
      shipping: cartData.positions.find((pos) => pos.type === "Shipping")?.amount.amount || "0.00",
      discount,
      handling: "0.00",
      taxTotal: "0.00",
      insurance: "0.00",
      shippingDiscount: "0.00",
    },
  } as PayPalCheckoutUpdatePaymentOptions;
}

export function setPayPalInfoOnViewState(
  nonce: string,
  context: ICartContext,
  paymentSource: FUNDING_SOURCE,
  place: PlaceType,
) {
  context.setViewState({
    ...context.viewState,
    paypalInfos: {
      nonce,
      paymentSource,
      place,
    },
    gPayData: undefined,
    appleData: undefined,
  });
}
